<template>
  <!--
    StatusItem.vue
    Displays a group of status chips for a user's registration and attendance.
    Shows attended years, attendee type, application status, RSVP, and special event participation.
  -->
  <div>
    <v-chip-group column>
      <!-- Attended years -->
      <v-tooltip v-for="(year, index) in attendedYears" :key="index" bottom>
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">
            <v-chip class="ma-2" color="primary" small label outlined>
              <v-icon small left>mdi-receipt-clock</v-icon>
              {{ year }}
            </v-chip>
          </span>
        </template>
        <span>DECODE {{ year }} ATTENDEE</span>
      </v-tooltip>

      <!-- Attendee type -->
      <v-chip class="ma-2" :color="isLastYearAttendee ? 'primary' : 'success'" small label outlined>
        <v-icon small left>{{ isLastYearAttendee ? 'mdi-page-previous' : 'mdi-new-box' }}</v-icon>
        {{ isLastYearAttendee ? 'Previous Attendee' : 'New Attendee' }}
      </v-chip>

      <!-- Blocked or Declined status -->
      <div v-if="isRejected">
        <v-chip class="ma-2" color="black" label text-color="white" small>
          <v-icon small left>mdi-cancel</v-icon>
          Blocked
        </v-chip>
      </div>
      <div v-else-if="applicationStatus === 'Declined'">
        <v-chip class="ma-2" color="error" label text-color="white" small>
          <v-icon small left>mdi-account-cancel</v-icon>
          Declined
        </v-chip>
      </div>

      <!-- Pending status -->
      <div v-else-if="applicationStatus === 'Pending'">
        <v-chip class="ma-2" color="warning" label text-color="white" small>
          <v-icon small left>mdi-account-clock</v-icon>
          Pending
        </v-chip>
      </div>

      <!-- Approved status -->
      <div v-else-if="applicationStatus === 'Approved'">
        <v-chip class="ma-2" color="success" label text-color="white" small>
          <v-icon small left>mdi-book-check</v-icon>
          Approved
        </v-chip>
      </div>

      <!-- No Application status -->
      <div v-else-if="applicationStatus === null">
        <v-chip class="ma-2" color="black" label text-color="white" small>
          <v-icon small left>mdi-note-remove</v-icon>
          No Application
        </v-chip>
      </div>

      <!-- RSVP and special event chips -->
      <v-chip-group class="ma-2" column>
        <!-- RSVP status -->
        <v-chip v-if="applicationStatus === 'Approved' && attendanceConfirmation === 'Yes'" small color="success" outlined label>
          <v-icon small left>mdi-email-check</v-icon>
          RSVP - Yes
        </v-chip>
        <v-chip v-else-if="applicationStatus === 'Approved' && attendanceConfirmation === 'No'" small color="error" outlined label>
          <v-icon small left>mdi-email-remove</v-icon>
          RSVP - No
        </v-chip>
        <v-chip v-else-if="applicationStatus === 'Approved'" small color="primary" outlined label>
          <v-icon small left>mdi-email-alert</v-icon>
          RSVP - Pending
        </v-chip>

        <!-- Special event participation -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip v-if="isHolRegistrant" small color="teal darken-4" label text-color="white" v-bind="attrs" v-on="on">
              <v-icon small left>mdi-desktop-classic</v-icon>
              HOL
            </v-chip>
          </template>
          <span>Hands On Lab Registrant</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip v-if="isGigRegistrant" small color="pink darken-4" label text-color="white" v-bind="attrs" v-on="on">
              <v-icon small left>mdi-face-woman</v-icon>
              GIG
            </v-chip>
          </template>
          <span>Get It Girls Registrant</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip v-if="isCtfRegistrant" small color="blue darken-4" label text-color="white" v-bind="attrs" v-on="on">
              <v-icon small left>mdi-flag</v-icon>
              CTF
            </v-chip>
          </template>
          <span>Capture the Flag Registrant</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip v-if="isVicOneRegistrant" small color="red darken-4" label text-color="white" v-bind="attrs" v-on="on">
              <v-icon small left>mdi-car-wireless</v-icon>
              VicOne
            </v-chip>
          </template>
          <span>VicOne Registrant</span>
        </v-tooltip>
      </v-chip-group>
    </v-chip-group>
  </div>
</template>

<script>
// StatusItem.vue: status chip group for user registration and attendance
export default {
  name: 'StatusItem',
  props: {
    /**
     * Whether the user's email is confirmed
     */
    emailConfirmed: { type: Boolean, required: true },
    /**
     * Whether the user attended last year
     */
    isLastYearAttendee: { type: Boolean, required: true },
    /**
     * Whether the user is blocked
     */
    isRejected: { type: Boolean, required: true },
    /**
     * Whether the user is confirmed
     */
    isConfirmed: { type: Boolean, required: true },
    /**
     * Whether the user is pending
     */
    isPending: { type: Boolean, required: true },
    /**
     * Whether the user is attending
     */
    isAttending: { type: Boolean, required: true },
    /**
     * Whether the user is attending AM session
     */
    isAttendingAmSession: { type: Boolean, required: true },
    /**
     * Whether the user is attending PM session
     */
    isAttendingPmSession: { type: Boolean, required: true },
    /**
     * Whether the user is attending Plenary
     */
    isAttendingPlenary: { type: Boolean, required: true },
    /**
     * Whether the user's attendance is finally confirmed
     */
    isFinalConfirmed: { type: Boolean, required: true },
    /**
     * Whether the user is a Hands On Lab registrant
     */
    isHolRegistrant: { type: Boolean, required: true },
    /**
     * Whether the user is a Get It Girls registrant
     */
    isGigRegistrant: { type: Boolean, required: true },
    /**
     * Whether the user is a Capture the Flag registrant
     */
    isCtfRegistrant: { type: Boolean, required: true },
    /**
     * Whether the user is a VicOne registrant
     */
    isVicOneRegistrant: { type: Boolean, required: true },
    /**
     * Profile verification status
     */
    profileVerificationStatus: { type: String, required: false, default: null },
    /**
     * Attendance confirmation status
     */
    attendanceConfirmation: { type: String, required: false, default: null },
    /**
     * Application status
     */
    applicationStatus: { type: String, required: false, default: null },
    /**
     * Years attended
     */
    attendedYears: { type: Array, required: false, default: () => [] },
  },
  methods: {
    /**
     * Returns a string describing the user's session attendance
     */
    attendingIdentifier(isAttendingAmSession, isAttendingPmSession, isAttendingPlenary) {
      if (isAttendingAmSession && isAttendingPmSession) return 'Attending AM and PM sessions';
      if (isAttendingAmSession) return 'Attending AM session';
      if (isAttendingPmSession) return 'Attending PM session';
      if (isAttendingPlenary) return 'Attending Plenary';
      return 'Attending';
    },
  },
};
</script>
